import { createContext, use, type ReactNode } from "react";
import { useOptionalAppAuth } from "~/lib/auth/app-auth";
import {
  usePortfolioExecutionFeed,
  type PortfolioExecutionFeedState,
  type UsePortfolioExecutionFeedOptions,
} from "../data/use-portfolio-execution-feed";

export type SharedPortfolioExecutionFeed = PortfolioExecutionFeedState & {
  readonly isConfigured: boolean;
};

type PortfolioExecutionFeedContextValue = {
  readonly feed: SharedPortfolioExecutionFeed;
  readonly portfolioId: string;
};

// Exported for focused provider tests. App consumers should use the shared
// hooks so a portfolio page owns exactly one socket.
export const PortfolioExecutionFeedContext =
  createContext<PortfolioExecutionFeedContextValue | null>(null);

export function PortfolioExecutionFeedProvider({
  children,
  portfolioId,
}: {
  readonly children: ReactNode;
  readonly portfolioId: string;
}) {
  const auth = useOptionalAppAuth();
  const feed = usePortfolioExecutionFeed(
    auth?.authenticated === true ? portfolioId : null,
  );

  // `children` keeps the stable identity supplied by the page. Feed updates
  // fan out only to context consumers instead of re-rendering every sibling in
  // the portfolio page subtree.
  return (
    <PortfolioExecutionFeedContext.Provider value={{ feed, portfolioId }}>
      {children}
    </PortfolioExecutionFeedContext.Provider>
  );
}

/**
 * Read the portfolio page's required shared feed. This hook is for consumers
 * that are always rendered under {@link PortfolioExecutionFeedProvider}.
 */
export function useSharedPortfolioExecutionFeed(): SharedPortfolioExecutionFeed {
  const shared = use(PortfolioExecutionFeedContext);
  if (!shared) {
    throw new Error("portfolio_execution_feed_provider_required");
  }
  return shared.feed;
}

/**
 * Reuse the page feed when the requested portfolio matches. Outside a
 * portfolio page (for example a standalone trade ticket), open a private feed
 * so the ticket retains its existing live-recovery behavior.
 */
export function useOptionalSharedPortfolioExecutionFeed(
  portfolioId: string | null | undefined,
  options: UsePortfolioExecutionFeedOptions = {},
): SharedPortfolioExecutionFeed {
  const shared = use(PortfolioExecutionFeedContext);
  const matchesSharedPortfolio =
    shared !== null && shared.portfolioId === portfolioId;
  const ownFeed = usePortfolioExecutionFeed(
    matchesSharedPortfolio ? null : portfolioId,
    options,
  );

  return matchesSharedPortfolio ? shared.feed : ownFeed;
}
